import { EmailTemplate } from '../constants/email'
import { OrganizationType } from '../constants/organization'
import { ExternalAccountKind } from '../constants/organizationRelationship'

export const allEmailTemplates: EmailTemplate[] = [
  EmailTemplate.ADMISSION_GROUP_RESERVATION_CREATED_STAFF,
  EmailTemplate.ADMISSION_GROUP_RESERVATION_REQUEST_CREATED_STAFF,
  EmailTemplate.ADMISSION_GROUP_RESERVATION_CANCELLED_STAFF,
  EmailTemplate.ORDER_PAYMENT_LINK_CONSUMER,
  EmailTemplate.CONFIRMATION_REQUEST_COMPLETED_STAFF,
  EmailTemplate.AFFILIATE_ACTIVATED,
  EmailTemplate.EVENT_ATTRIBUTION_LINKS_STAFF,
  EmailTemplate.ORDER_CANCELLED_CUSTOMER,
  EmailTemplate.CHAT_NEEDS_RESPONSE_MANAGEMENT,
  EmailTemplate.ORDER_CONFIRMATION_CONSUMER,
  EmailTemplate.ORGANIZATION_ACCESS_ADDED_MANAGEMENT,
  EmailTemplate.PASSWORD_RESET_CONSUMER,
  EmailTemplate.CONSUMER_LOGIN_CODE,
  EmailTemplate.MANAGEMENT_LOGIN_CODE,
  EmailTemplate.PASSWORD_RESET_MANAGEMENT,
  EmailTemplate.USER_INVITED_MANAGEMENT,
  EmailTemplate.USER_INVITED_PROMOTER_MANAGEMENT,
  EmailTemplate.USER_INVITED_PARTNER_MANAGEMENT,
  EmailTemplate.USER_REGISTERED_CONSUMER,
  EmailTemplate.USER_REGISTERED_MANAGEMENT,
  EmailTemplate.USER_REGISTERED_PROMOTER_MANAGEMENT,
  EmailTemplate.USER_REGISTERED_PARTNER_MANAGEMENT,
  EmailTemplate.SCHEDULED_REPORT_ADMISSION_GROUPS_SUMMARY_VENUE,
  EmailTemplate.SCHEDULED_REPORT_UPCOMING_EVENT_TICKET_SUMMARY_VENUE,
  EmailTemplate.SCHEDULED_REPORT_DAY_SUMMARY,
  EmailTemplate.SCHEDULED_REPORT_CSV_EXPORT_MANAGEMENT,
  EmailTemplate.DATA_EXPORT_COMPLETED_MANAGEMENT,
  EmailTemplate.DATA_EXPORT_FAILED_MANAGEMENT,
  EmailTemplate.NEW_ACCOUNT_SIGNUP_ADMINS,
  EmailTemplate.NEW_EXTERNAL_PROMOTER_SIGNUP_ADMINS,
  EmailTemplate.NEW_EXTERNAL_PARTNER_SIGNUP_ADMINS,
  EmailTemplate.ORDER_REFUNDED_CUSTOMER,
  EmailTemplate.ORDER_VOIDED_CUSTOMER,
  EmailTemplate.ORDER_VOIDED_CUSTOMER_LEGACY,
  EmailTemplate.CONCIERGE_MESSAGE_USER,
  EmailTemplate.SUPPORT_AGENT_MESSAGE_USER,
  EmailTemplate.ADMIN_APP_INSTALL_LINK_USER,
  EmailTemplate.AFFILIATE_SIGNUP_INTERNAL,
]

export type GetDefaultEmailTemplateTestDataOptions = {
  recipientEmail?: string
  /** When set (e.g. `'[Test]'`), title becomes `${titlePrefix} ${template}`. */
  titlePrefix?: string
}

function buildTitle(
  template: EmailTemplate,
  options?: GetDefaultEmailTemplateTestDataOptions
): string {
  if (options?.titlePrefix !== undefined) {
    return `${options.titlePrefix} ${template}`
  }
  return `Email test — ${template}`
}

function reservationConfirmationStub(
  template: EmailTemplate,
  recipientEmail: string,
  options?: GetDefaultEmailTemplateTestDataOptions
): Record<string, unknown> & { title: string } {
  return {
    title: buildTitle(template, options),
    venueName: 'Test Venue',
    venueUrl: 'https://example.com/venue',
    venueLogo: undefined,
    bookingSubject: 'Test reservation',
    bookingDate: 'Saturday, Jan 15',
    address: '123 Test St',
    arrivalTime: '8:00 PM',
    guests: '4',
    user: { firstName: 'Test', name: 'Test User', email: recipientEmail },
    bookingItems: [
      {
        name: 'Table for 4',
        description: 'Booth',
        price: 200,
        quantity: 1,
      },
    ],
    bookingBreakdown: {
      price: 200,
      tax: 0,
      tip: 0,
      total: 200,
      discounts: [],
      fees: [],
    },
  }
}

export function getDefaultEmailTemplateTestData(
  template: EmailTemplate,
  options?: GetDefaultEmailTemplateTestDataOptions
): Record<string, unknown> & { title: string } {
  const recipientEmail = options?.recipientEmail ?? 'test@example.com'
  const base = { title: buildTitle(template, options) }

  switch (template) {
    case EmailTemplate.PASSWORD_RESET_CONSUMER:
    case EmailTemplate.PASSWORD_RESET_MANAGEMENT:
      return {
        ...base,
        passwordResetUrl: 'https://example.com/reset?token=stub',
        venueName: 'Test Venue',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
        organizationName: 'Test Org',
      }
    case EmailTemplate.CONSUMER_LOGIN_CODE:
    case EmailTemplate.MANAGEMENT_LOGIN_CODE:
      return {
        ...base,
        loginCode: '123456',
        venueName: 'Test Venue',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
      }
    case EmailTemplate.USER_REGISTERED_CONSUMER:
      return {
        ...base,
        name: 'Test User',
        venueName: 'Test Venue',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
      }
    case EmailTemplate.ORDER_CONFIRMATION_CONSUMER:
      return {
        ...reservationConfirmationStub(template, recipientEmail, options),
        downloadTicketUrl: 'https://example.com/tickets/stub',
        tickets: [
          {
            id: 'order_ticket_1',
            label: 'ADMIT ONE',
            name: 'Order Guest #1',
            row: 'A4',
            seat: '12',
            priceDisplay: '$25.00',
            eventName: 'Summer Concert',
            eventDateDisplay: 'July 15, 2026',
            eventTimeDisplay: '8:00 PM',
            barcodeDisplay: '839472 90284 34028 93847 28',
            token: 'order_token_1',
            qrCode:
              'https://cdn-images.tablelist.com/qr/booking/booking_1/order_token_1.png',
            walletPassUrl: 'https://example.com/wallet/order_ticket_1',
          },
        ],
      }
    case EmailTemplate.CHAT_NEEDS_RESPONSE_MANAGEMENT:
      return {
        ...base,
        guestName: 'Test Guest',
        venueName: 'Test Venue',
        lastMessage: 'This is a test chat message.',
        customerChatThreadUrl: 'https://example.com/chat/stub',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
      }
    case EmailTemplate.ADMISSION_GROUP_RESERVATION_CREATED_STAFF:
    case EmailTemplate.ADMISSION_GROUP_RESERVATION_REQUEST_CREATED_STAFF:
    case EmailTemplate.ADMISSION_GROUP_RESERVATION_CANCELLED_STAFF:
      return {
        ...base,
        guestName: 'Test Guest',
        date: 'Saturday Jan 15',
        venueName: 'Test Venue',
        reservationUrl: 'https://example.com/reservations/test',
        tableNumbers: 'Booth 12',
        notes: 'Birthday celebration',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
      }
    case EmailTemplate.ORDER_PAYMENT_LINK_CONSUMER:
      return {
        ...base,
        name: 'Test Guest',
        guestName: 'Test Guest',
        date: 'Saturday Jan 15',
        venueName: 'Test Venue',
        description: 'Please complete your reservation payment.',
        paymentAmountTypeBlurb: 'Pay $100 deposit',
        confirmationRequestUrl: 'https://example.com/confirmation/stub',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
      }
    case EmailTemplate.CONFIRMATION_REQUEST_COMPLETED_STAFF:
      return {
        ...base,
        guestName: 'Test Guest',
        date: 'Saturday Jan 15',
        venueName: 'Test Venue',
        paymentAmountTypeBlurb: 'Paid $100 deposit',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
      }
    case EmailTemplate.AFFILIATE_ACTIVATED:
      return {
        ...base,
        name: 'Test Affiliate',
      }
    case EmailTemplate.EVENT_ATTRIBUTION_LINKS_STAFF:
      return {
        ...base,
        venueName: 'Test Venue',
        eventName: 'Summer Concert',
        eventDate: 'Saturday Jan 15',
        notes: 'Share this with your audience.',
        eventUrl: 'https://example.com/events/test?attributionId=abc',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
      }
    case EmailTemplate.ORGANIZATION_ACCESS_ADDED_MANAGEMENT:
      return {
        ...base,
        organizationName: 'Test Org',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
      }
    case EmailTemplate.USER_INVITED_MANAGEMENT:
    case EmailTemplate.USER_INVITED_PROMOTER_MANAGEMENT:
    case EmailTemplate.USER_INVITED_PARTNER_MANAGEMENT:
      return {
        ...base,
        organizationName: 'Test Org',
        passwordResetUrl: 'https://example.com/invite?token=stub',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
      }
    case EmailTemplate.USER_REGISTERED_MANAGEMENT:
    case EmailTemplate.USER_REGISTERED_PROMOTER_MANAGEMENT:
    case EmailTemplate.USER_REGISTERED_PARTNER_MANAGEMENT:
      return {
        ...base,
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
      }
    case EmailTemplate.ORDER_CANCELLED_CUSTOMER:
      return {
        ...base,
        cancelledDisplayText: 'Your reservation was cancelled.',
        cancelledNote: 'Test cancellation note.',
        venueName: 'Test Venue',
        type: 'reservation',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
      }
    case EmailTemplate.SCHEDULED_REPORT_ADMISSION_GROUPS_SUMMARY_VENUE:
      return {
        ...base,
        venueName: 'Test Venue',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
        dateRange: 'Jan 1 – Jan 7',
        reportTime: '9:00 AM EST',
        admissionGroupGeneralStats: [
          ['', 'Expected', 'Final'],
          ['Reservations', '10', '8'],
          ['Guests', '40', '32'],
          ['Minimums', '$2,000', '$1,800'],
          ['Spend', '', '$1,750'],
        ],
        admissionGroupsPending: [
          {
            guestName: 'Guest One',
            tableName: 'Booth 1',
            guestsExpected: 4,
            minimumSpend: 500,
            spent: null,
            arrivalTime: '8:00 PM',
          },
        ],
        admissionGroupsClosed: [
          {
            guestName: 'Guest Two',
            tableName: 'Table 2',
            guestsExpected: 2,
            minimumSpend: 200,
            spent: 220,
            arrivalTime: '7:00 PM',
          },
        ],
        admissionGroupAttributionStats: [
          [
            'Attributed to',
            'Completed / Booked',
            'Min Spend',
            'Final Spend',
            'Final Spend %',
          ],
          ['Source A', '5 / 6', '$500', '$480', '45.2%'],
        ],
      }
    case EmailTemplate.SCHEDULED_REPORT_UPCOMING_EVENT_TICKET_SUMMARY_VENUE:
      return {
        ...base,
        venueName: 'Test Venue',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
        eventStats: [
          {
            eventName: 'Test Event',
            eventDate: '01/15',
            csvLink: 'https://example.com/report.csv',
            stats: [
              ['Tickets Sold', 'Tickets Remaining', 'Ticket Sales'],
              ['50', '100', '$2,500'],
            ],
          },
        ],
      }
    case EmailTemplate.SCHEDULED_REPORT_DAY_SUMMARY:
      return {
        ...base,
        venueName: 'Test Venue',
        venueUrl: 'https://example.com/venue',
        venueLogo: undefined,
        reportDate: '01/15/2025',
        includeReservations: true,
        includeAdmisions: true,
        venueCurrencyCode: 'USD',
        venueLanguageCode: 'en',
        admissionGroupStats: {
          totalGuests: 75,
          totalSpent: 5200,
          totalReservations: 8,
          totalExpectedReservations: 10,
          totalTicketGuests: 50,
          totalExpectedTicketGuests: 60,
          totalTickets: 45,
          totalExpectedTickets: 55,
          totalGuestlists: 5,
          totalExpectedGuestlists: 5,
          totalTallies: 0,
          totalExpectedTallies: 0,
          totalReservationSpent: 2400,
          totalAdmissionSpent: 2800,
        },
        inventoryStats: {
          hasPackageSales: true,
          packageSales: [
            {
              name: 'Booth A',
              price: 500,
              quantitySold: 3,
              quantityTotal: 5,
              percentSold: 60,
            },
          ],
          hasTicketSales: true,
          ticketSales: [
            {
              name: 'GA',
              price: 25,
              quantitySold: 40,
              quantityTotal: 100,
              percentSold: 40,
            },
          ],
        },
        eventName: 'Test Event',
        eventStart: '9:00 PM',
        eventEnd: '2:00 AM',
      }
    case EmailTemplate.SCHEDULED_REPORT_CSV_EXPORT_MANAGEMENT:
      return {
        ...base,
        reportName: 'Weekly Reservations',
        venueName: 'Test Venue',
        dataExportName: 'Reservations Export',
        dataDownloadUrl: 'https://example.com/exports/scheduled-report.csv',
        filterSummaryLines: [
          'Date range preset: Previous 7 days',
          'Organization: Test Org',
          'Venues: Main Room',
        ],
      }
    case EmailTemplate.DATA_EXPORT_COMPLETED_MANAGEMENT:
      return {
        ...base,
        dataExportName: 'Customer Export',
        dataDownloadUrl: 'https://example.com/exports/test.csv',
      }
    case EmailTemplate.DATA_EXPORT_FAILED_MANAGEMENT:
      return {
        ...base,
        dataExportName: 'Customer Export',
      }
    case EmailTemplate.NEW_ACCOUNT_SIGNUP_ADMINS:
      return {
        ...base,
        venueName: 'Test Venue',
        organizationName: 'Test Venue Org',
        organizationType: OrganizationType.STANDARD_VENUE,
        organizationCreatedAt: '2026-01-01T10:00:00.000Z',
        organizationAddress: '123 Test St, New York NY 10001, US',
        organizationPortalUrl: 'https://example.com/admin/organizations/test',
      }
    case EmailTemplate.NEW_EXTERNAL_PROMOTER_SIGNUP_ADMINS:
      return {
        ...base,
        organizationName: 'Test Promoter',
        organizationType: OrganizationType.LIMITED_EXTERNAL,
        organizationAccountKind: ExternalAccountKind.PROMOTER,
        organizationCreatedAt: '2026-01-01T10:00:00.000Z',
        organizationAddress: '123 Test St, New York NY 10001, US',
        organizationPortalUrl: 'https://example.com/admin/organizations/test',
      }
    case EmailTemplate.NEW_EXTERNAL_PARTNER_SIGNUP_ADMINS:
      return {
        ...base,
        organizationName: 'Test Partner',
        organizationType: OrganizationType.LIMITED_EXTERNAL,
        organizationAccountKind: ExternalAccountKind.PARTNER,
        organizationCreatedAt: '2026-01-01T10:00:00.000Z',
        organizationAddress: '123 Test St, New York NY 10001, US',
        organizationPortalUrl: 'https://example.com/admin/organizations/test',
      }
    case EmailTemplate.NEW_EXTERNAL_PROMOTER_ADDED_ADMINS:
      return {
        ...base,
        organizationName: 'Test Promoter Added',
        organizationType: OrganizationType.LIMITED_EXTERNAL,
        organizationAccountKind: ExternalAccountKind.PROMOTER,
        organizationCreatedAt: '2026-01-01T10:00:00.000Z',
        organizationAddress: '123 Test St, New York NY 10001, US',
        organizationPortalUrl: 'https://example.com/admin/organizations/test',
      }
    case EmailTemplate.NEW_EXTERNAL_PARTNER_ADDED_ADMINS:
      return {
        ...base,
        organizationName: 'Test Partner Added',
        organizationType: OrganizationType.LIMITED_EXTERNAL,
        organizationAccountKind: ExternalAccountKind.PARTNER,
        organizationCreatedAt: '2026-01-01T10:00:00.000Z',
        organizationAddress: '123 Test St, New York NY 10001, US',
        organizationPortalUrl: 'https://example.com/admin/organizations/test',
      }
    case EmailTemplate.ORDER_VOIDED_CUSTOMER_LEGACY:
    case EmailTemplate.ORDER_VOIDED_CUSTOMER:
    case EmailTemplate.CONCIERGE_MESSAGE_USER:
    case EmailTemplate.SUPPORT_AGENT_MESSAGE_USER:
    case EmailTemplate.ADMIN_APP_INSTALL_LINK_USER:
    case EmailTemplate.AFFILIATE_SIGNUP_INTERNAL:
      return {
        ...base,
        bodyHtml:
          '<p>This is <strong>stub</strong> HTML body content for preview.</p>',
      }
    case EmailTemplate.ORDER_REFUNDED_CUSTOMER:
      return {
        ...reservationConfirmationStub(template, recipientEmail, options),
      }
    default:
      return base
  }
}
